import { cloneDeep, isNil } from 'lodash';
import { v4 as uuid } from 'uuid';
import { LocalizedTextField, i18nKeyType } from '../../../../../i18n';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { logger } from '../../../../../util/logger';
import { QNear, Waypoint } from '../../../../../util/machine-variable.utils';
import {
  FineTunedPointRequired,
  MotionPoint,
  MotionType,
  PartUtil,
} from '../../../../../util/part.util';
import { PickerOption } from '../../../shared/component/widget/widget.types';
import { MoveToPointProps } from '../../../shared/hook/move-to-point.hooks';
import {
  FineTuneProps,
  Flow,
  FlowItem,
  FlowItemArrayType,
  FlowItemMetadata,
  FlowItemParameter,
  FlowItemParameterMetadata,
  FlowItemParameterRef,
  FlowItemText,
  FlowSelectedItemInfo,
  SystemUnit,
  UnsavedFlowItemSectionParent,
  UnsavedNestedFlowItem,
} from '../../../shared/model/flow.types';
import { AbstractFlowItemService } from '../../../shared/service/abstract-flow-item.service';
import {
  ConvertMotionPointToFlowItemParams,
  ConvertMotionPointsToFlowItemSectionParams,
  ConvertMotionPointsToFlowItemSectionsParams,
} from '../../../shared/service/service.types';
import {
  incrementItemStats,
  instanceOfFlowItem,
  instanceOfFlowItemSection,
  instanceOfFlowItemSectionParent,
  instanceOfHelper,
} from '../../../shared/util/flow-item.util';
import {
  addStickoutToPose,
  convertParameterValueToUOM,
  getParameterValue,
} from '../../../shared/util/flow-parameter.util';
import { getNextChar } from '../../../shared/util/string';
import { BeaconWeldingData } from '../model/beacon-welding-data';
import { isWeldType, sectionBreaksSequence } from '../util/beacon-welding.util';

class WeldingFlowItemServiceImpl extends AbstractFlowItemService<
  BeaconWeldingData.FlowConfig,
  typeof BeaconWeldingData.FlowSectionType
> {
  constructor(
    items: FlowItemMetadata[],
    private welder: BeaconWeldingData.Welder,
    parameterAdjustmentMetadata?: BeaconWeldingData.AppFlowItemParameterRefAdjustmentMetadata
  ) {
    super(
      items,
      BeaconWeldingData.FlowItemRef.WELD,
      parameterAdjustmentMetadata
    );
  }

  public canConvertFlowItemToMultipass(item: FlowItem): boolean {
    const isApproach = getParameterValue<boolean>(
      item,
      BeaconWeldingData.FlowItemParameterRef.APPROACH
    );

    const isSupportedWeldType = this.isMultipassSupportedMotionType(item);

    return isApproach && isSupportedWeldType;
  }

  public canConvertFlowItemToStitch(item: FlowItem): boolean {
    const isApproach = getParameterValue<boolean>(
      item,
      BeaconWeldingData.FlowItemParameterRef.APPROACH
    );

    const isSupportedWeldType = this.isStitchSupportedMotionType(item);

    return isApproach && isSupportedWeldType;
  }

  public isMultipassSupportedMotionType(item: FlowItem): boolean {
    const weldType = getParameterValue<BeaconWeldingData.WeldTypeEnum>(
      item,
      BeaconWeldingData.FlowItemParameterRef.WELD_TYPE
    );

    const supportedWeldTypes = [
      BeaconWeldingData.WeldTypeEnum.LINEAR,
      BeaconWeldingData.WeldTypeEnum.RADIAL,
      BeaconWeldingData.WeldTypeEnum.ROTARY,
    ];

    return supportedWeldTypes.includes(weldType);
  }

  public isStitchSupportedMotionType(item: FlowItem): boolean {
    const weldType = getParameterValue<BeaconWeldingData.WeldTypeEnum>(
      item,
      BeaconWeldingData.FlowItemParameterRef.WELD_TYPE
    );

    const supportedWeldTypes = [
      BeaconWeldingData.WeldTypeEnum.LINEAR,
      BeaconWeldingData.WeldTypeEnum.RADIAL,
    ];

    return supportedWeldTypes.includes(weldType);
  }

  public convertFlowItemsToMotionPoints(
    items: FlowItem[],
    options?: {
      addStickoutToPose?: boolean;
    }
  ): MotionPoint[] {
    const motionPoints: MotionPoint[] = [];

    for (const item of items) {
      if (item.ref === BeaconWeldingData.FlowItemRef.SECTION) {
        continue;
      }

      const waypoint = getParameterValue<Waypoint>(
        item,
        BeaconWeldingData.FlowItemParameterRef.WAYPOINT
      );

      if (isNil(waypoint)) {
        throw new Error(
          `Failed to convert flow item to stitch point. Waypoint is undefined. Item ID: ${item.id}`
        );
      }

      const qnear = getParameterValue<QNear>(
        item,
        BeaconWeldingData.FlowItemParameterRef.QNEAR
      );

      if (isNil(qnear)) {
        throw new Error(
          `Failed to convert flow item to stitch point. QNear is undefined. Item ID: ${item.id}`
        );
      }

      const motionType = this.getMotionType(item);

      let pose = PartUtil.convertWaypointToPose(waypoint);

      if (options?.addStickoutToPose) {
        const stickout =
          getParameterValue<number>(
            item,
            BeaconWeldingData.FlowItemParameterRef.STICKOUT
          ) ?? 0;

        pose = addStickoutToPose(pose, stickout);
      }

      const point: MotionPoint = {
        pose,
        qnear,
        type: motionType,
      };

      motionPoints.push(point);
    }

    return motionPoints;
  }

  /**
   * Converts an array of motion points to an array of flow items.
   *
   * The end of a section is determined by the presence of an AIR motion point.
   */
  public convertMotionPointsToFlowItemSections(
    params: ConvertMotionPointsToFlowItemSectionsParams<
      typeof BeaconWeldingData.FlowSectionType
    >
  ): FlowItem[] {
    const { motionPoints, defaultItem, sectionDetails } = params;

    const { sectionType, nameKey, numericPrefix, labelSuffixType } =
      sectionDetails;

    const flowItems: FlowItem[] = [];
    let currentSection = this.createSectionFlowItemForType(sectionType);
    let currentLabelSuffix = labelSuffixType
      ? this.getNextFlowItemSectionSuffix(labelSuffixType)
      : '';

    currentSection.name = this.buildFlowItemSectionName({
      nameKey,
      numericPrefix,
      suffix: currentLabelSuffix,
    });

    flowItems.push(currentSection);

    for (let index = 0; index < motionPoints.length; index++) {
      const point = motionPoints[index];

      const item = this.convertMotionPointToFlowItem({
        point,
        defaultItem,
      });

      flowItems.push(item);

      if (point.type === MotionType.AIR && index < motionPoints.length - 1) {
        currentSection = this.createSectionFlowItemForType(sectionType);
        currentLabelSuffix = labelSuffixType
          ? this.getNextFlowItemSectionSuffix(
              labelSuffixType,
              currentLabelSuffix
            )
          : '';
        currentSection.name = this.buildFlowItemSectionName({
          nameKey,
          numericPrefix: numericPrefix,
          suffix: currentLabelSuffix,
        });

        flowItems.push(currentSection);
      }
    }

    return flowItems;
  }

  /**
   * Converts an array of motion points to an array of flow items constituting
   * a single section sequence.
   */
  public convertMotionPointsToFlowItemSection(
    params: ConvertMotionPointsToFlowItemSectionParams<
      typeof BeaconWeldingData.FlowSectionType
    >
  ): FlowItem[] {
    const { motionPoints, defaultItem, sectionDetails } = params;
    const { sectionType, name } = sectionDetails;

    const flowItems: FlowItem[] = [];

    const section = this.createSectionFlowItemForType(sectionType);

    section.name = name;

    flowItems.push(section);

    for (let index = 0; index < motionPoints.length; index++) {
      const point = motionPoints[index];

      const item = this.convertMotionPointToFlowItem({
        point,
        defaultItem,
      });

      flowItems.push(item);
    }

    return flowItems;
  }

  public convertMotionPointToFlowItem(
    params: ConvertMotionPointToFlowItemParams
  ): FlowItem {
    const { point, defaultItem } = params;
    const { pose, qnear, type } = point;

    const itemRef = this.getStitchPointApplicationItemRef(point);
    const newItem = this.getDefaultItemForRef(
      itemRef,
      type === MotionType.AIR ? undefined : defaultItem
    );

    const waypoint = PartUtil.convertPoseToWaypoint(pose);
    const itemName = this.getItemName(type);

    this.setParameterValue<Waypoint>(
      newItem,
      BeaconWeldingData.FlowItemParameterRef.WAYPOINT,
      waypoint
    );
    this.setParameterValue<QNear>(
      newItem,
      BeaconWeldingData.FlowItemParameterRef.QNEAR,
      qnear
    );

    if (type === MotionType.AIR) {
      this.setParameterValue(
        newItem,
        BeaconWeldingData.FlowItemParameterRef.MOVE_SPEED_ADJUSTMENT_RATIO,
        0.5
      );
    } else {
      const motionType = this.convertMotionTypeToApplicationMotionValue(type);
      this.setParameterValue(
        newItem,
        BeaconWeldingData.FlowItemParameterRef.WELD_TYPE,
        motionType
      );
    }

    newItem.id = uuid();
    newItem.name = itemName;

    return newItem;
  }

  public getRequiredParametersForTeaching(
    ref: string
  ): FlowItemParameterMetadata[] {
    const item = this.getItemMetadata(ref);
    const params: FlowItemParameterMetadata[] = [];
    if (ref === BeaconWeldingData.FlowItemRef.WELD) {
      const type = item?.availableParameters.find((p) => p.apiName === 'type');
      if (type) {
        params.push(type);
      }
    }
    return params;
  }

  public recalculateParameterValues(
    config: BeaconWeldingData.FlowConfig,
    items: FlowItem[],
    options?: {
      isDefaultParameterSet?: boolean;
    }
  ): FlowItem[] {
    const { isDefaultParameterSet = false } = options ?? {};
    let contiguousWelds = 0;
    let itemIdx = 0;

    for (const item of items) {
      const itemMetadata = this.getItemMetadata(item.ref);

      if (item.ref !== BeaconWeldingData.FlowItemRef.SECTION && itemMetadata) {
        const singleItemWeld = this.isSinglePrimaryActionItem(item);

        if (
          item.ref === BeaconWeldingData.FlowItemRef.WELD &&
          !singleItemWeld
        ) {
          contiguousWelds++;
        } else {
          contiguousWelds = 0;
        }

        const parameterMetadataMap = this.getParametersMetadataMap(item.ref);

        for (const parameter of item.parameters) {
          const parameterMetadata = parameterMetadataMap[parameter.ref];

          if (parameterMetadata?.calculateValue) {
            parameter.value = parameterMetadata.calculateValue(
              config,
              item,
              parameter.value,
              items
            );
          }
        }

        // if the parameter set is the default parameter set it is neither an
        // approach nor a depart
        if (isDefaultParameterSet) {
          this.setParameterValue(
            item,
            BeaconWeldingData.FlowItemParameterRef.APPROACH,
            false
          );
          this.setParameterValue(
            item,
            BeaconWeldingData.FlowItemParameterRef.DEPART,
            false
          );
        } else if (singleItemWeld) {
          this.setParameterValue(
            item,
            BeaconWeldingData.FlowItemParameterRef.APPROACH,
            true
          );
          this.setParameterValue(
            item,
            BeaconWeldingData.FlowItemParameterRef.DEPART,
            true
          );
        } else {
          this.setParameterValue(
            item,
            BeaconWeldingData.FlowItemParameterRef.APPROACH,
            contiguousWelds === 1
          );

          if (contiguousWelds >= 1) {
            // we need to find the next item that is not a sequence breaker
            // if an item isn't found, we are at the end
            let nextItemBreaksSequence = true;

            for (let i = itemIdx + 1; i < items.length; i++) {
              const nextItem = items[i];

              if (instanceOfFlowItemSection(nextItem)) {
                if (sectionBreaksSequence(nextItem)) {
                  nextItemBreaksSequence = true;
                  break;
                }
              } else {
                nextItemBreaksSequence =
                  isNil(nextItem) ||
                  nextItem.ref !== BeaconWeldingData.FlowItemRef.WELD ||
                  this.isSinglePrimaryActionItem(nextItem);
                break;
              }
            }

            if (nextItemBreaksSequence) {
              this.setParameterValue(
                item,
                BeaconWeldingData.FlowItemParameterRef.DEPART,
                true
              );
            } else {
              this.setParameterValue(
                item,
                BeaconWeldingData.FlowItemParameterRef.DEPART,
                false
              );
            }
          } else {
            this.setParameterValue(
              item,
              BeaconWeldingData.FlowItemParameterRef.DEPART,
              false
            );
          }
        }
      }

      itemIdx++;
    }

    return items;
  }

  public getFineTuneProps(
    flow: Flow<BeaconWeldingData.FlowConfig>,
    item: FlowItem
  ): FineTuneProps {
    let supported = false;

    const itemType = getParameterValue<BeaconWeldingData.WeldTypeEnum>(
      item,
      BeaconWeldingData.FlowItemParameterRef.WELD_TYPE
    );

    if (item.ref === BeaconWeldingData.FlowItemRef.WELD) {
      if (
        ![
          BeaconWeldingData.WeldTypeEnum.TACK,
          BeaconWeldingData.WeldTypeEnum.ROTARY,
        ].includes(itemType)
      ) {
        supported = true;
      }
    }

    const contiguousItems = this.getContiguousItemsForFlowItem(
      item,
      flow.items
    );

    const stickout =
      getParameterValue<number>(
        item,
        BeaconWeldingData.FlowItemParameterRef.STICKOUT
      ) ?? 0;

    const itemIndex = contiguousItems.findIndex((i) => i.id === item.id);
    const executingIndex = flow.items.findIndex((i) => i.id === item?.id);

    let isLast = false;

    if (itemIndex === contiguousItems.length - 1) {
      isLast = true;
    }

    let startItem: FlowItem = item;
    let midItem: FlowItem | undefined;
    let endItem: FlowItem | undefined;
    let radial: boolean = false;

    let pointToProcess = FineTunedPointRequired.START_POINT;

    // example logic for radial points - 5a, 5b, 5c, 5d
    // 5a - start (current selection)
    // 5b - mid
    // 5c - end
    //
    // 5b - start (current selection)
    // 5c - mid
    // 5d - end
    //
    // 5b - start
    // 5c - mid (current selection)
    // 5d - end
    //
    // 5b - start
    // 5c - mid
    // 5d - end (current selection)

    if (itemType === BeaconWeldingData.WeldTypeEnum.RADIAL) {
      radial = true;

      const previous = contiguousItems[itemIndex - 1];
      const next = contiguousItems[itemIndex + 1];

      if (
        previous === undefined ||
        !isWeldType(previous, BeaconWeldingData.WeldTypeEnum.RADIAL)
      ) {
        startItem = item;
        midItem = next;
        endItem = contiguousItems[itemIndex + 2];
      } else if (next === undefined) {
        startItem = contiguousItems[itemIndex - 2];
        midItem = previous;
        endItem = item;

        pointToProcess = FineTunedPointRequired.END_POINT;
      } else if (
        previous !== undefined &&
        isWeldType(previous, BeaconWeldingData.WeldTypeEnum.RADIAL)
      ) {
        const itemAfterNext = contiguousItems[itemIndex + 2];
        if (
          itemAfterNext === undefined ||
          !isWeldType(next, BeaconWeldingData.WeldTypeEnum.RADIAL)
        ) {
          startItem = previous;
          midItem = item;
          endItem = next;

          pointToProcess = FineTunedPointRequired.MID_POINT;
        } else if (isWeldType(next, BeaconWeldingData.WeldTypeEnum.RADIAL)) {
          startItem = item;
          midItem = next;
          endItem = itemAfterNext;

          pointToProcess = FineTunedPointRequired.START_POINT;
        }
      }
    } else {
      startItem = item;
      const previous = contiguousItems[itemIndex - 1];
      const next = contiguousItems[itemIndex + 1];
      if (isLast) {
        endItem = previous;
        pointToProcess = FineTunedPointRequired.END_POINT;
      } else {
        endItem = next;
        midItem = previous;
      }
    }

    const workPiecePosition =
      item.attributes?.workSurfacePosition ??
      this.getInferredWorkSurfacePosition({
        item,
        flowItems: flow.items ?? [],
      });

    const workPieceOffset = item.attributes?.workSurfaceOffset ?? 0;

    const fineTuneProps: FineTuneProps = {
      supported: supported,
      type: radial ? 'radial' : 'linear',
      pointToProcess: pointToProcess,
      startPoint: getParameterValue(
        startItem,
        BeaconWeldingData.FlowItemParameterRef.WAYPOINT
      ),
      midPoint: midItem
        ? getParameterValue(
            midItem,
            BeaconWeldingData.FlowItemParameterRef.WAYPOINT
          )
        : undefined,
      endPoint: endItem
        ? getParameterValue(
            endItem,
            BeaconWeldingData.FlowItemParameterRef.WAYPOINT
          )
        : undefined,
      toolConfig: flow.config.torch,
      stickout,
      poseIncludesStickout: false,
      workPiecePosition,
      workPieceOffset,
      ui: {
        path: {
          label: 'welding:weld_path',
        },
        stickout: {
          label: 'welding:stickout_offset',
        },
        travelAngle: {
          label: 'welding:travel_angle',
        },
        workAngle: {
          label: 'welding:work_angle',
        },
        applyTo: {
          label: 'welding:fine_tune_apply_to',
        },
      },
      executingIndex: executingIndex,
    };

    return fineTuneProps;
  }

  public getMoveToPointProps(
    flow: Flow<BeaconWeldingData.FlowConfig>,
    item?: FlowItem<string> | undefined
  ): MoveToPointProps {
    let moveToPointProps: MoveToPointProps = {};

    const isMoveHome = item?.ref === BeaconWeldingData.FlowItemRef.MOVE_HOME;

    let point: Waypoint | undefined = undefined;
    if (item) {
      point = isMoveHome
        ? flow.config.home.waypoint
        : getParameterValue<Waypoint>(
            item,
            BeaconWeldingData.FlowItemParameterRef.WAYPOINT
          );
    }

    if (point && item) {
      const qnear = isMoveHome
        ? flow.config.home.qnear
        : getParameterValue<QNear>(
            item,
            BeaconWeldingData.FlowItemParameterRef.QNEAR
          );
      const stickout = isMoveHome
        ? 0
        : getParameterValue<number>(
            item,
            BeaconWeldingData.FlowItemParameterRef.STICKOUT
          );

      const itemIndex = flow.items.findIndex((i) => i.id === item?.id);

      moveToPointProps = {
        point,
        qnear,
        stickout,
        toolConfig: flow.config.torch,
        executingIndex: itemIndex,
      };
    }

    return moveToPointProps;
  }

  public buildFlowItemTextArr(
    flowItems: FlowItemArrayType[]
  ): (FlowItemText | undefined)[] {
    let currentSectionIdx = 0;
    let currentItemIdx = 1;
    let currentAlpha = 'a';
    let prevItem: FlowItem | undefined;

    return flowItems.map<FlowItemText | undefined>((item) => {
      if (instanceOfHelper(item) || !item.id) {
        return undefined;
      }

      let filteredName = item.name?.replace(/ - \d+$/, '') ?? '';

      if (instanceOfFlowItemSection(item)) {
        if (
          item.attributes?.type === BeaconWeldingData.FlowSectionType.STITCH ||
          item.attributes?.type === BeaconWeldingData.FlowSectionType.MULTIPASS
        ) {
          return {
            itemId: item.id,
            index: '',
            name: filteredName,
            label: filteredName,
          };
        }

        currentSectionIdx++;

        return {
          itemId: item.id,
          index: `${currentSectionIdx}`,
          name: filteredName,
          label: `${currentSectionIdx} - ${filteredName}`,
        };
      }

      if (item.ref === BeaconWeldingData.FlowItemRef.WAIT_FOR) {
        const type = getParameterValue(
          item,
          BeaconWeldingData.FlowItemParameterRef.WAIT_FOR_TYPE_OF_INPUT
        );

        if (type === BeaconWeldingData.WaitForInputType.OPERATOR_BUTTON) {
          const message = getParameterValue<string>(
            item,
            BeaconWeldingData.FlowItemParameterRef.WAIT_FOR_OPERATOR_MESSAGE
          );
          const defaultItem = this.getDefaultItemForRef(
            BeaconWeldingData.FlowItemRef.WAIT_FOR
          );
          const defaultName = this.generateFlowItemName(
            BeaconWeldingData.FlowItemRef.WAIT_FOR
          );
          const defaultMessage = getParameterValue<string>(
            defaultItem,
            BeaconWeldingData.FlowItemParameterRef.WAIT_FOR_OPERATOR_MESSAGE
          );
          if (filteredName === defaultName) {
            if (message?.length > 0 && message !== defaultMessage) {
              filteredName = message;
            }
          }
        }
      }

      const isFirst = prevItem === undefined;
      const singleItemWeld = this.isSinglePrimaryActionItem(item);

      if (item.ref !== BeaconWeldingData.FlowItemRef.WELD || singleItemWeld) {
        // this logic handles non-welds and single point welds
        if (!isFirst) {
          ++currentItemIdx;
        }

        prevItem = item;

        return {
          itemId: item.id,
          index: `${currentItemIdx}`,
          name: filteredName,
          label: `${currentItemIdx} - ${filteredName}`,
        };
      } else {
        // this logic handles contiguous welds
        if (isFirst) {
          prevItem = item;

          return {
            itemId: item.id,
            index: `${currentItemIdx}${currentAlpha}`,
            name: filteredName,
            label: `${currentItemIdx}${currentAlpha} - ${filteredName}`,
          };
        } else {
          // the only non-sequence breaking item is a contiguous welds
          // at this point, prevItem should always be set
          const prevIsNotSequenceBreaking =
            prevItem?.ref === BeaconWeldingData.FlowItemRef.WELD &&
            !this.isSinglePrimaryActionItem(prevItem);

          if (prevIsNotSequenceBreaking) {
            currentAlpha = getNextChar(currentAlpha);
          } else {
            currentAlpha = 'a';
            ++currentItemIdx;
          }

          prevItem = item;
          return {
            itemId: item.id,
            index: `${currentItemIdx}${currentAlpha}`,
            name: filteredName,
            label: `${currentItemIdx}${currentAlpha} - ${filteredName}`,
          };
        }
      }
    });
  }

  public isShiftableItem(item: FlowItem): boolean {
    if (
      item.ref === BeaconWeldingData.FlowItemRef.MOVE ||
      item.ref === BeaconWeldingData.FlowItemRef.WELD
    ) {
      return true;
    }

    return false;
  }

  public getFlowSelectedItemInfo = (
    flow: Flow,
    selectedItems: string[]
  ): FlowSelectedItemInfo => {
    const stats: FlowSelectedItemInfo = {
      linear: { count: 0, selected: 0 },
      linearStitch: { count: 0, selected: 0 },
      other: { count: 0, selected: 0 },
      radial: { count: 0, selected: 0 },
      rotary: { count: 0, selected: 0 },
      tack: { count: 0, selected: 0 },
    };

    for (let index = 0; index < flow?.items?.length; index++) {
      const item = flow.items[index];

      if (item.id !== undefined) {
        if (item.ref === BeaconWeldingData.FlowItemRef.WELD) {
          const weldType = getParameterValue<BeaconWeldingData.WeldTypeEnum>(
            item,
            BeaconWeldingData.FlowItemParameterRef.WELD_TYPE
          );

          if (weldType === BeaconWeldingData.WeldTypeEnum.TACK) {
            stats.tack = incrementItemStats(
              stats.tack,
              selectedItems.includes(item.id)
            );
          } else if (weldType === BeaconWeldingData.WeldTypeEnum.LINEAR) {
            stats.linear = incrementItemStats(
              stats.linear,
              selectedItems.includes(item.id)
            );
          } else if (weldType === BeaconWeldingData.WeldTypeEnum.RADIAL) {
            stats.radial = incrementItemStats(
              stats.radial,
              selectedItems.includes(item.id)
            );
          } else if (
            weldType === BeaconWeldingData.WeldTypeEnum.LINEAR_STITCH
          ) {
            stats.linearStitch = incrementItemStats(
              stats.linearStitch,
              selectedItems.includes(item.id)
            );
          } else if (weldType === BeaconWeldingData.WeldTypeEnum.ROTARY) {
            stats.rotary = incrementItemStats(
              stats.rotary,
              selectedItems.includes(item.id)
            );
          }
        } else {
          stats.other = incrementItemStats(
            stats.other,
            selectedItems.includes(item.id)
          );
        }
      }
    }

    return stats;
  };

  public getFlowItemSubheading = (
    flowItem: FlowItem,
    system?: SystemUnit
  ): string | undefined => {
    let subheading: string | undefined;

    if (flowItem.ref === BeaconWeldingData.FlowItemRef.WELD) {
      subheading = this.getWeldItemSubheading(flowItem, system);
    } else if (flowItem.ref === BeaconWeldingData.FlowItemRef.MOVE) {
      subheading = this.getMoveItemSubheading();
    } else if (flowItem.ref === BeaconWeldingData.FlowItemRef.MOVE_HOME) {
      subheading = this.getMoveHomeItemSubheading();
    } else if (flowItem.ref === BeaconWeldingData.FlowItemRef.WAIT_FOR) {
      subheading = this.getWaitForItemSubheading(flowItem);
    } else if (
      flowItem.ref === BeaconWeldingData.FlowItemRef.PRODUCTION_REPORTING
    ) {
      subheading = this.getProductionItemSubheading(flowItem);
    } else if (flowItem.ref === BeaconWeldingData.FlowItemRef.TRIGGER_OUTPUT) {
      subheading = this.getTriggerOutputItemSubheading(flowItem);
    }

    return subheading;
  };

  public getApplicationDefaultParameterSetImplicitTags(): Record<
    string,
    unknown
  > {
    // TODO
    return {};
  }

  public getContiguousItemsForFlowItem(
    item: FlowItem,
    items: FlowItem[]
  ): FlowItem[] {
    if (item.ref === BeaconWeldingData.FlowItemRef.WELD) {
      const start = this.getStartOfSequenceIndexFromFlowItem(item, items);
      const end = this.getEndOfSequenceIndexFromFlowItem(item, items);
      if (start !== undefined && end !== undefined) {
        return items.slice(start, end + 1);
      }
    }

    return [];
  }

  public getStartOfSequenceIndexFromFlowItem(
    flowItem: FlowItem,
    flowItems: FlowItem[]
  ): number | undefined {
    let startIndex: number | undefined = undefined;
    const flowItemIndex: number | undefined = flowItem.id
      ? flowItems.findIndex((i) => i.id !== undefined && i.id === flowItem.id)
      : undefined;
    if (flowItemIndex !== undefined && isWeldType(flowItem)) {
      if (this.hasApproach(flowItem)) {
        startIndex = flowItemIndex;
      } else {
        for (let i = flowItemIndex; i >= 0; i--) {
          const item = flowItems[i];
          if (
            !this.isOfType(BeaconWeldingData.FlowItemRef.SECTION, item) &&
            isWeldType(item) &&
            this.hasApproach(item)
          ) {
            startIndex = i;
            break;
          }
        }
      }
    }
    return startIndex;
  }

  public getEndOfSequenceIndexFromFlowItem(
    flowItem: FlowItem,
    flowItems: FlowItem[]
  ): number | undefined {
    let endIndex: number | undefined = undefined;
    const flowItemIndex: number | undefined = flowItem.id
      ? flowItems.findIndex((i) => i.id !== undefined && i.id === flowItem.id)
      : undefined;
    if (flowItemIndex !== undefined && isWeldType(flowItem)) {
      if (this.hasDepart(flowItem)) {
        endIndex = flowItemIndex;
      } else {
        for (let i = flowItemIndex; i <= flowItems.length; i++) {
          const item = flowItems[i];
          if (
            !this.isOfType(BeaconWeldingData.FlowItemRef.SECTION, item) &&
            isWeldType(item) &&
            this.hasDepart(item)
          ) {
            endIndex = i;
            break;
          }
        }
      }
    }
    return endIndex;
  }

  public getEndOfSequenceItemFromFlowItem(
    flowItem: FlowItem,
    flowItems: FlowItem[]
  ): FlowItem | undefined {
    const endIndex = this.getEndOfSequenceIndexFromFlowItem(
      flowItem,
      flowItems
    );
    return endIndex !== undefined ? flowItems[endIndex] : undefined;
  }

  public getCopyOfParentSectionItems = (items: FlowItem[]): FlowItem[] => {
    const [sectionItem, ...childItems] = items;

    if (instanceOfFlowItemSectionParent(sectionItem)) {
      const parentSectionTempId = uuid();

      // Extract the original type ensuring it's of the correct enum type
      const sectionType = sectionItem.attributes
        ?.type as BeaconWeldingData.FlowSectionType;

      // Create a properly typed object with all required properties
      const modifiedSectionItem: UnsavedFlowItemSectionParent<BeaconWeldingData.FlowSectionType> =
        {
          ...sectionItem,
          attributes: {
            ...sectionItem.attributes,
            type: sectionType,
            tempId: parentSectionTempId,
            endItemId: undefined,
            originalItems:
              sectionItem.attributes?.originalItems?.map((i) => {
                return {
                  ...i,
                  id: uuid(),
                };
              }) || [],
            isParent: true,
            creationIndex: sectionItem.attributes.creationIndex,
          },
        };

      const modifiedChildren = childItems.map((item) => {
        if (instanceOfFlowItem(item)) {
          const newItem: UnsavedNestedFlowItem = {
            ...item,
            attributes: {
              ...item?.attributes,
              // this is to allow the unsaved sections to search for an end item
              parentSectionTempId,
            },
          };

          return newItem;
        }
        return item;
      });

      return [modifiedSectionItem, ...modifiedChildren];
    }

    return items;
  };

  public getCopyWithNestedItems = (items: FlowItem[]): FlowItem[] => {
    const copiedItems = cloneDeep(items);
    const modifiedItems: FlowItem[] = [];

    for (let i = 0; i < copiedItems.length; i++) {
      const item = copiedItems[i];
      if (instanceOfFlowItemSectionParent(item)) {
        const endItemId = item.attributes?.endItemId;
        const endItemIndex = endItemId
          ? copiedItems.findIndex((copiedItem) => copiedItem.id === endItemId)
          : undefined;

        if (endItemIndex === undefined) {
          logger.error(
            'Parent section has no end item id. Skipping section item.',
            {
              item,
            }
          );
          continue;
        }

        if (endItemIndex === -1) {
          logger.error(
            'End item for parent section could not be found. Skipping section item.',
            {
              item,
            }
          );
          continue;
        }

        if (endItemIndex <= i) {
          logger.error(
            'End item index is invalid (less than or equal to current index). Skipping section item.',
            {
              item,
              currentIndex: i,
              endItemIndex,
            }
          );
          continue;
        }

        const sectionItems = copiedItems.slice(i, endItemIndex + 1);

        modifiedItems.push(...this.getCopyOfNestedItems(sectionItems));
        i += sectionItems.length - 1;
      } else {
        modifiedItems.push(item);
      }
    }

    return modifiedItems;
  };

  public getCopyOfNestedItems = (items: FlowItem[]): FlowItem[] => {
    const sectionItem = items[0];

    if (instanceOfFlowItemSectionParent(sectionItem)) {
      return this.getCopyOfParentSectionItems(items);
    }

    return items;
  };

  public handlePasteFlowItems(flowItems: FlowItem[]): FlowItem[] {
    const hasSectionParent = flowItems.some((item) =>
      instanceOfFlowItemSectionParent(item)
    );

    let items: FlowItem[];

    if (hasSectionParent) {
      items = this.getCopyWithNestedItems(flowItems);
    } else {
      items = cloneDeep(flowItems);
    }

    items = items.map((item) => {
      item.id = undefined;
      return item;
    });

    return items;
  }

  public isSinglePrimaryActionItem(item: FlowItem): boolean {
    if (item.ref === this.getDefaultItemRef()) {
      const singleItemWelds = [
        BeaconWeldingData.WeldTypeEnum.TACK,
        BeaconWeldingData.WeldTypeEnum.ROTARY,
      ];

      const weldType = getParameterValue<BeaconWeldingData.WeldTypeEnum>(
        item,
        BeaconWeldingData.FlowItemParameterRef.WELD_TYPE
      );

      return singleItemWelds.includes(weldType);
    }

    return false;
  }

  protected hasApproach(item: FlowItem): boolean {
    const parameter: FlowItemParameter<boolean> | undefined =
      item.parameters.find(
        (p) => p.ref === BeaconWeldingData.FlowItemParameterRef.APPROACH
      );
    if (parameter) {
      return parameter.value;
    }

    return false;
  }

  protected hasDepart(item: FlowItem): boolean {
    const parameter: FlowItemParameter<boolean> | undefined =
      item.parameters.find(
        (p) => p.ref === BeaconWeldingData.FlowItemParameterRef.DEPART
      );
    if (parameter) {
      return parameter.value;
    }

    return false;
  }

  protected getMoveItemSubheading = (): string => {
    const subheading = getLocalizedText(
      BeaconWeldingData.BeaconMoveItem.name
    ) as string;

    return subheading;
  };

  protected getMoveHomeItemSubheading = (): string => {
    const subheading = getLocalizedText(
      BeaconWeldingData.BeaconMoveHomeItem.name
    ) as string;

    return subheading;
  };

  protected getWaitForItemSubheading = (flowItem: FlowItem): string => {
    const waitForLabel = getLocalizedText(
      BeaconWeldingData.BeaconWaitForItem.name
    ) as string;

    const type = getParameterValue(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.WAIT_FOR_TYPE_OF_INPUT
    );

    const typeOption: PickerOption | undefined =
      BeaconWeldingData.WaitForTypeMetadata.ui?.widgetOptions.find(
        (o: PickerOption) => {
          return o.value === type;
        }
      );

    const typeLabel = getLocalizedText(
      typeOption?.label as i18nKeyType
    )?.toLowerCase();

    if (type === BeaconWeldingData.WaitForInputType.SENSOR) {
      const inputLabel = this.getFlowItemPickerLabel(
        flowItem,
        BeaconWeldingData.FlowItemParameterRef.WAIT_FOR_INPUT
      )?.toLowerCase();

      const stateLabel = this.getFlowItemPickerLabel(
        flowItem,
        BeaconWeldingData.FlowItemParameterRef.WAIT_FOR_INPUT_STATE
      )?.toLowerCase();

      return `${waitForLabel} ${inputLabel} - ${stateLabel}`;
    } else if (type === BeaconWeldingData.WaitForInputType.TIME) {
      const time = getParameterValue(
        flowItem,
        BeaconWeldingData.FlowItemParameterRef.WAIT_FOR_TIME
      );
      return `${waitForLabel} ${time}s`;
    } else if (
      type === BeaconWeldingData.WaitForInputType.OPERATOR_BUTTON ||
      typeLabel === undefined
    ) {
      let suffix = typeLabel;

      // backwards compatibility for wait for operator label where we don't have a type
      if (typeLabel === undefined) {
        const option = this.getFlowItemPickerOptionByValue(
          flowItem,
          BeaconWeldingData.FlowItemParameterRef.WAIT_FOR_TYPE_OF_INPUT,
          BeaconWeldingData.WaitForInputType.OPERATOR_BUTTON
        );

        if (option) {
          suffix = getLocalizedText(option.label as i18nKeyType)?.toLowerCase();
        }
      }

      return `${waitForLabel} ${suffix}`;
    }

    return waitForLabel;
  };

  protected getTriggerOutputItemSubheading = (flowItem: FlowItem): string => {
    const triggerText = getLocalizedText('welding:trigger');
    const outputText = getLocalizedText('welding:output')?.toLowerCase();

    const outputLabel = this.getFlowItemPickerLabel(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.TRIGGER_OUTPUT_PORT
    )?.toLowerCase();

    const stateLabel = this.getFlowItemPickerLabel(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.TRIGGER_OUTPUT_STATE
    )?.toLowerCase();

    return `${triggerText} ${outputLabel} ${outputText} - ${stateLabel}`;
  };

  protected getProductionItemSubheading = (flowItem: FlowItem): string => {
    const qty = this.getWeldItemNumberValue(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.PRODUCTION_QUANTITY
    );

    const subheading = getLocalizedText(
      BeaconWeldingData.BeaconReportProductionItem.name
    ) as string;

    return `${subheading} (${getLocalizedText(
      'welding:quantity_abbr'
    )} ${qty})`;
  };

  protected getWeldItemSubheading = (
    flowItem: FlowItem,
    system?: SystemUnit
  ): string => {
    const subheadingParts: string[] = [];
    const isTack = isWeldType(flowItem, BeaconWeldingData.WeldTypeEnum.TACK);
    const isRotary = isWeldType(
      flowItem,
      BeaconWeldingData.WeldTypeEnum.ROTARY
    );
    const isDepart = this.hasDepart(flowItem);

    if (isTack) {
      return this.getTackWeldLabel(flowItem, system);
    }

    if (isRotary) {
      return this.getRotaryWeldLabel(flowItem);
    }

    if (isDepart) {
      const craterFillTime = this.getWeldItemNumberValue(
        flowItem,
        BeaconWeldingData.FlowItemParameterRef.CRATER_FILL_TIME
      );

      const craterFillText = `${craterFillTime}s ${getLocalizedText(
        BeaconWeldingData.CraterFillTimeMetadata.name
      )?.toLowerCase()}`;

      return `${getLocalizedText('welding:end_of_weld')} (${craterFillText})`;
    }

    const weldTypeText = this.getFlowItemPickerLabel(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.WELD_TYPE
    );
    if (weldTypeText) {
      subheadingParts.push(weldTypeText);
    }

    const processOption = this.getFlowItemPickerOption(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.PROCESS
    );

    if (processOption) {
      let processSettings = '';
      if (
        processOption.value === BeaconWeldingData.WeldProcess.PULSE ||
        processOption.value === BeaconWeldingData.WeldProcess.MILLER_RMD
      ) {
        processSettings = this.getWeldItemPulseProcess(flowItem, system);
      } else if (
        processOption.value === BeaconWeldingData.WeldProcess.OTC_WAVE_PULSE
      ) {
        processSettings = this.getWeldItemWavePulseProcess(flowItem, system);
      } else {
        processSettings = this.getWeldItemCVProcess(flowItem, system);
      }
      const label = getLocalizedText(processOption.label as i18nKeyType);

      subheadingParts.push(`${label} (${processSettings})`);
    }

    return subheadingParts.join(', ');
  };

  protected getTackWeldLabel = (
    flowItem: FlowItem,
    system?: SystemUnit
  ): string => {
    const settings: string[] = [];
    const weldTypeText = this.getFlowItemPickerLabel(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.WELD_TYPE
    );

    const processOption = this.getFlowItemPickerOption(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.PROCESS
    );

    let processSettings = '';

    if (processOption) {
      if (processOption.value === BeaconWeldingData.WeldProcess.PULSE) {
        const arclength = this.getWeldItemNumberValue(
          flowItem,
          BeaconWeldingData.FlowItemParameterRef.ARC_LENGTH
        );

        if (arclength) {
          settings.push(arclength);
        }
      } else {
        const voltage = this.getWeldItemNumberValue(
          flowItem,
          BeaconWeldingData.FlowItemParameterRef.VOLTAGE
        );

        if (voltage && voltage !== 'null') {
          settings.push(`${voltage}V`);
        }
      }

      let wfs = this.getWeldItemNumberValue(
        flowItem,
        BeaconWeldingData.FlowItemParameterRef.WIRE_FEED_SPEED
      );

      if (wfs) {
        if (system) {
          const convertedWFS = convertParameterValueToUOM(
            BeaconWeldingData.WireFeedSpeedMetadata.value,
            system,
            Number(wfs),
            false
          );
          wfs = `${convertedWFS}`;
        }

        settings.push(`${wfs} wfs`);
      }

      const tackHold = this.getWeldItemNumberValue(
        flowItem,
        BeaconWeldingData.FlowItemParameterRef.TACK_HOLD
      );

      if (tackHold) {
        const tackHoldText = `${tackHold}s ${getLocalizedText(
          BeaconWeldingData.TackHoldMetadata.name
        )?.toLowerCase()}`;

        settings.push(tackHoldText);
      }

      const craterFillTime = this.getWeldItemNumberValue(
        flowItem,
        BeaconWeldingData.FlowItemParameterRef.CRATER_FILL_TIME
      );

      if (craterFillTime) {
        const craterFillText = `${craterFillTime}s ${getLocalizedText(
          BeaconWeldingData.CraterFillTimeMetadata.name
        )?.toLowerCase()}`;

        settings.push(craterFillText);
      }

      const label = getLocalizedText(processOption.label as i18nKeyType);

      processSettings += `${label} (${settings.join(', ')})`;
    }

    return `${weldTypeText}, ${processSettings}`;
  };

  protected getTravelSpeedLabel = (
    flowItem: FlowItem,
    system?: SystemUnit
  ): string | undefined => {
    const travel = this.getWeldItemNumberValue(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.TRAVEL_SPEED
    );

    let travelSpeedText: string | undefined;

    if (travel) {
      if (system) {
        const convertedTravelSpeed = convertParameterValueToUOM(
          BeaconWeldingData.TravelSpeedMetadata.value,
          system,
          Number(travel),
          false
        );
        travelSpeedText = `${convertedTravelSpeed} ${
          system === SystemUnit.IMPERIAL ? 'ipm' : 'cpm'
        }`;
      } else {
        travelSpeedText = travel;
      }
    }

    return travelSpeedText;
  };

  protected getRotaryWeldLabel = (flowItem: FlowItem): string => {
    const settings: string[] = [];
    const weldTypeText = this.getFlowItemPickerLabel(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.WELD_TYPE
    );

    const processOption = this.getFlowItemPickerOption(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.PROCESS
    );

    let processSettings = '';

    if (processOption) {
      if (processOption.value === BeaconWeldingData.WeldProcess.PULSE) {
        const arclength = this.getWeldItemNumberValue(
          flowItem,
          BeaconWeldingData.FlowItemParameterRef.ARC_LENGTH
        );

        if (arclength) {
          settings.push(arclength);
        }
      } else {
        const voltage = this.getWeldItemNumberValue(
          flowItem,
          BeaconWeldingData.FlowItemParameterRef.VOLTAGE
        );

        if (voltage && voltage !== 'null') {
          settings.push(`${voltage}V`);
        }
      }

      const wfs = this.getWeldItemNumberValue(
        flowItem,
        BeaconWeldingData.FlowItemParameterRef.WIRE_FEED_SPEED
      );

      if (wfs) {
        settings.push(`${wfs} wfs`);
      }

      const arcStart = this.getWeldItemNumberValue(
        flowItem,
        BeaconWeldingData.FlowItemParameterRef.ARC_START_TIME
      );

      if (arcStart) {
        const arcStartText = `${arcStart}s ${getLocalizedText(
          BeaconWeldingData.ArcStartTimeMetadata.name
        )?.toLowerCase()}`;

        settings.push(arcStartText);
      }

      const craterFill = this.getWeldItemNumberValue(
        flowItem,
        BeaconWeldingData.FlowItemParameterRef.CRATER_FILL_TIME
      );

      if (craterFill) {
        const craterFillText = `${craterFill}s ${getLocalizedText(
          BeaconWeldingData.CraterFillTimeMetadata.name
        )?.toLowerCase()}`;

        settings.push(craterFillText);
      }

      const label = getLocalizedText(processOption.label as i18nKeyType);

      processSettings += `${label} (${settings.join(', ')})`;
    }

    return `${weldTypeText}, ${processSettings}`;
  };

  protected getWFSLabel = (
    flowItem: FlowItem,
    system?: SystemUnit
  ): string | undefined => {
    let wfs = this.getWeldItemNumberValue(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.WIRE_FEED_SPEED
    );

    if (wfs) {
      if (system) {
        const convertedWFS = convertParameterValueToUOM(
          BeaconWeldingData.WireFeedSpeedMetadata.value,
          system,
          Number(wfs),
          false
        );
        wfs = `${convertedWFS}`;
      }

      return `${wfs} wfs`;
    }
  };

  protected getWeldItemNumberValue = (
    flowItem: FlowItem,
    parameterRef: FlowItemParameterRef
  ): string | undefined => {
    let settingText: string | undefined;
    const param = flowItem.parameters.find((p) => p.ref === parameterRef);

    if (param) {
      settingText = `${param.value}`;
    }

    return settingText;
  };

  protected getWeldItemWavePulseProcess = (
    flowItem: FlowItem,
    system?: SystemUnit
  ): string => {
    const settingsTextArray: string[] = [];

    const waveFreq = this.getWeldItemNumberValue(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.WAVE_FREQUENCY
    );

    if (waveFreq !== undefined) {
      settingsTextArray.push(`${waveFreq} Hz`);
    }

    const synergicMode = getParameterValue<boolean>(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.SYNERGIC_MODE
    );

    if (!synergicMode) {
      const voltage = this.getWeldItemNumberValue(
        flowItem,
        BeaconWeldingData.FlowItemParameterRef.VOLTAGE
      );

      if (voltage && voltage !== 'null') {
        settingsTextArray.push(`${voltage}V`);
      }
    }

    // This is only related to OTC
    const useWFS = getParameterValue(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.USE_WIRE_FEED_SPEED
    );

    const evaluateCurrent =
      this.welder === BeaconWeldingData.Welder.OTC_WELBEE && !useWFS;

    if (evaluateCurrent) {
      const current = this.getWeldItemNumberValue(
        flowItem,
        BeaconWeldingData.FlowItemParameterRef.WELDING_CURRENT
      );
      settingsTextArray.push(`${current} amps`);
    } else {
      const wfsLabel = this.getWFSLabel(flowItem, system);

      if (wfsLabel) {
        settingsTextArray.push(wfsLabel);
      }
    }

    const travelSpeedText = this.getTravelSpeedLabel(flowItem, system);

    if (travelSpeedText) {
      settingsTextArray.push(travelSpeedText);
    }

    return settingsTextArray.join(', ');
  };

  protected getWeldItemPulseProcess = (
    flowItem: FlowItem,
    system?: SystemUnit
  ): string => {
    const settingsTextArray: string[] = [];

    const isOTC = this.welder === BeaconWeldingData.Welder.OTC_WELBEE;
    if (isOTC) {
      const synergicMode = getParameterValue<boolean>(
        flowItem,
        BeaconWeldingData.FlowItemParameterRef.SYNERGIC_MODE
      );
      if (!synergicMode) {
        const voltage = this.getWeldItemNumberValue(
          flowItem,
          BeaconWeldingData.FlowItemParameterRef.VOLTAGE
        );

        if (voltage && voltage !== 'null') {
          settingsTextArray.push(`${voltage}V`);
        }
      }
    } else {
      const arcLength = this.getWeldItemNumberValue(
        flowItem,
        BeaconWeldingData.FlowItemParameterRef.ARC_LENGTH
      );

      if (arcLength) {
        settingsTextArray.push(arcLength);
      }
    }

    // This is only related to OTC
    const useWFS = getParameterValue(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.USE_WIRE_FEED_SPEED
    );

    const evaluateCurrent =
      this.welder === BeaconWeldingData.Welder.OTC_WELBEE && !useWFS;

    if (evaluateCurrent) {
      const current = this.getWeldItemNumberValue(
        flowItem,
        BeaconWeldingData.FlowItemParameterRef.WELDING_CURRENT
      );
      settingsTextArray.push(`${current} amps`);
    } else {
      const wfsLabel = this.getWFSLabel(flowItem, system);

      if (wfsLabel) {
        settingsTextArray.push(wfsLabel);
      }
    }

    const travelSpeedText = this.getTravelSpeedLabel(flowItem, system);

    if (travelSpeedText) {
      settingsTextArray.push(travelSpeedText);
    }

    return settingsTextArray.join(', ');
  };

  protected getWeldItemCVProcess = (
    flowItem: FlowItem,
    system?: SystemUnit
  ): string => {
    const settingsTextArray: string[] = [];

    const voltage = this.getWeldItemNumberValue(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.VOLTAGE
    );
    if (voltage) {
      settingsTextArray.push(`${voltage}V`);
    }

    const wfsLabel = this.getWFSLabel(flowItem, system);

    if (wfsLabel) {
      settingsTextArray.push(wfsLabel);
    }

    const travelSpeedText = this.getTravelSpeedLabel(flowItem, system);

    if (travelSpeedText) {
      settingsTextArray.push(travelSpeedText);
    }

    return settingsTextArray.join(', ');
  };

  protected getMotionType(item: FlowItem): MotionType {
    const motionType = getParameterValue<number>(
      item,
      BeaconWeldingData.FlowItemParameterRef.WELD_TYPE
    );

    if (motionType === BeaconWeldingData.WeldTypeEnum.LINEAR) {
      return MotionType.LINEAR;
    } else if (motionType === BeaconWeldingData.WeldTypeEnum.RADIAL) {
      return MotionType.RADIAL;
    } else if (motionType === BeaconWeldingData.WeldTypeEnum.ROTARY) {
      return MotionType.ROTARY;
    } else if (motionType === undefined) {
      return MotionType.AIR;
    }

    logger.info('Weld motion type is not supported. Converting to air move.', {
      motionType,
    });
    return MotionType.AIR;
  }

  protected getStitchPointApplicationItemRef(point: MotionPoint): string {
    const { type } = point;

    return type === MotionType.AIR
      ? BeaconWeldingData.FlowItemRef.MOVE
      : BeaconWeldingData.FlowItemRef.WELD;
  }

  protected getItemName(type: MotionType): string {
    if (type === MotionType.AIR) {
      return getLocalizedText('welding:move') ?? 'Air Move';
    } else {
      return getLocalizedText('welding:weld') ?? 'Weld';
    }
  }

  protected convertMotionTypeToApplicationMotionValue(
    motionType: MotionType
  ): number {
    if (motionType === MotionType.LINEAR) {
      return BeaconWeldingData.WeldTypeEnum.LINEAR;
    } else if (motionType === MotionType.RADIAL) {
      return BeaconWeldingData.WeldTypeEnum.RADIAL;
    } else if (motionType === MotionType.ROTARY) {
      return BeaconWeldingData.WeldTypeEnum.ROTARY;
    }

    throw new Error(`Motion type is not defined: ${motionType}`);
  }

  protected createSectionFlowItemForType(
    type: BeaconWeldingData.FlowSectionType
  ): FlowItem<string, BeaconWeldingData.FlowItemPathSectionAttributes> {
    const sectionItem =
      this.getDefaultItemForRef<BeaconWeldingData.FlowItemPathSectionAttributes>(
        BeaconWeldingData.FlowItemRef.SECTION
      );

    sectionItem.id = uuid();
    sectionItem.attributes = {
      type,
    };

    return sectionItem;
  }

  protected getNextFlowItemSectionSuffix(
    type: 'char' | 'number',
    currentSuffix?: string | number
  ): string | number {
    if (currentSuffix === undefined) {
      return type === 'number' ? 1 : 'a';
    }

    if (typeof currentSuffix === 'number') {
      return currentSuffix + 1;
    } else {
      return getNextChar(currentSuffix);
    }
  }

  protected buildFlowItemSectionName(params: {
    nameKey: i18nKeyType | LocalizedTextField;
    numericPrefix: number | undefined;
    suffix: string | number;
  }): string {
    const { nameKey, numericPrefix, suffix } = params;
    const labelNamePrefix = getLocalizedText(nameKey);

    return numericPrefix
      ? `${labelNamePrefix} ${numericPrefix}${suffix}`
      : `${labelNamePrefix} ${suffix}`;
  }
}

export { WeldingFlowItemServiceImpl };
